import type { DelayBroadcastPattern } from '../../../api/types';
import { CONNECTING_STATION_GRANULARITY_DEFAULT_SSML } from '../../../utils/broadcastContents';
import { createDefaultPattern } from '../logic';
import { PlaceholderLegend } from '../parts/PlaceholderLegend';
import { CONNECTING_STATION_DIRECTION_PLACEHOLDERS } from '../placeholders';
import { DelayPatternRow } from './DelayPatternRow';

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ブロック内の「遅延放送パターン表」。
 *
 * 自列車遅延の `SelfTrainDelayFields` / part1 の `ConnectingLineDelayPatternsTable` と
 * 同じ `DelayPatternRow` を再利用し、遅れ分数の幅ごとに 1 行ずつ
 * 「遅れ分数 / 放送粒度 / 放送パターン (方面部分文SSML) / 行操作 (＋−)」を表示する。
 *
 * ここで編集する `ssml_template` は **方面部分文** (`{directions_text}` に流し込まれる素材)
 * である点が part1 の transfer_aware 用テーブルと違う。placeholder / 凡例も part2 用に切り替える。
 */
export function ConnectingStationLineDelayPatternsTable({
  lineIndex,
  patterns,
  onChange,
}: {
  /** 親接続線ブロックの index (data-testid 用) */
  lineIndex: number;
  patterns: DelayBroadcastPattern[];
  onChange: (nextPatterns: DelayBroadcastPattern[]) => void;
}): React.JSX.Element {
  // 空配列の場合は最低 1 行を表示 (part1 と同じポリシー)。
  const rows: DelayBroadcastPattern[] =
    patterns.length > 0
      ? patterns
      : [createDefaultPattern(CONNECTING_STATION_GRANULARITY_DEFAULT_SSML)];

  const updatePattern = (index: number, patch: Partial<DelayBroadcastPattern>): void => {
    const next = rows.slice();
    const current = next[index]!;
    // 粒度が変わったら接続線 part2 用のデフォルト方面部分文に上書きする
    // (ユーザ要望: 「粒度を変えたら元々のデフォルト値を再設定して」)
    if (patch.granularity && patch.granularity !== current.granularity) {
      next[index] = {
        ...current,
        ...patch,
        ssml_template: CONNECTING_STATION_GRANULARITY_DEFAULT_SSML[patch.granularity],
      };
    } else {
      next[index] = { ...current, ...patch };
    }
    onChange(next);
  };

  const addPatternAfter = (index: number): void => {
    const next = rows.slice();
    next.splice(index + 1, 0, createDefaultPattern(CONNECTING_STATION_GRANULARITY_DEFAULT_SSML));
    onChange(next);
  };

  const removePattern = (index: number): void => {
    if (rows.length <= 1) return;
    const next = rows.slice();
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <div
      className="connecting-station-line-delay-patterns"
      data-testid={`connecting-station-line-delay-patterns-${lineIndex}`}
      style={{ marginTop: 8 }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>遅延放送パターン (方面部分文 SSML)</div>
      <table className="delay-patterns-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              遅れ分数
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              放送粒度
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px' }}>
              放送パターン (方面部分文 SSML)
            </th>
            <th
              aria-label="行操作"
              style={{ textAlign: 'center', padding: '4px 6px', whiteSpace: 'nowrap' }}
            />
          </tr>
        </thead>
        <tbody>
          {rows.map((pattern, i) => (
            <DelayPatternRow
              key={i}
              index={i}
              pattern={pattern}
              canRemove={rows.length > 1}
              ssmlTestIdPrefix={`connecting-station-line-${lineIndex}-direction-ssml`}
              placeholderTemplates={CONNECTING_STATION_GRANULARITY_DEFAULT_SSML}
              onUpdate={(patch) => updatePattern(i, patch)}
              onAdd={() => addPatternAfter(i)}
              onRemove={() => removePattern(i)}
            />
          ))}
        </tbody>
      </table>
      <p className="hint" style={{ margin: '8px 0 4px' }}>
        遅れ分数の幅ごとに「方面部分文 SSML」を設定します。この文言が各方面ごとに展開され、
        読点で連結されて接続線ブロックの <code>{'{directions_text}'}</code> に埋め込まれます。
      </p>
      <PlaceholderLegend
        testId={`connecting-station-line-direction-hint-${lineIndex}`}
        items={CONNECTING_STATION_DIRECTION_PLACEHOLDERS}
      />
    </div>
  );
}
